import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinct, distinctUntilChanged, map } from 'rxjs/operators';
import { ObjectEventEmitters } from '../events/object.events';
import { TimeShiftLockItem } from '../models/timeShiftLockModel';
import { ObjectTypes } from '../enums/objectTypes';

@Injectable({
  providedIn: 'root',
})
export class TimeShiftLockService implements OnDestroy {
  private lockedObjects = new Map<string, TimeShiftLockItem>();
  private lockedObjectsSubject = new BehaviorSubject<Map<string, TimeShiftLockItem>>(new Map());
  private subscription: Subscription = new Subscription();

  constructor(private readonly objectEventEmitters: ObjectEventEmitters) {
    this.initSubscription();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private initSubscription(): void {
    this.subscription.add(
      this.objectEventEmitters.timeShiftLockUpdated$.subscribe((items: TimeShiftLockItem[]) => {
        this.updateLockedObjects(items);
      })
    );
  }

  private updateLockedObjects(items: TimeShiftLockItem[]): void {
    items.forEach((item) => {
      const key = this.getObjectKey(item.objectType, item.objectId);

      if (item.isLocked) {
        this.lockedObjects.set(key, item);
      } else {
        this.lockedObjects.delete(key);
      }
    });

    this.lockedObjectsSubject.next(new Map(this.lockedObjects));
  }

  private getObjectKey(objectType: ObjectTypes, objectId: number): string {
    return `${objectType}_${objectId}`;
  }

  public isObjectLocked(objectType: ObjectTypes, objectId: number): boolean {
    const key = this.getObjectKey(objectType, objectId);
    return this.lockedObjects.has(key);
  }

  public isObjectLocked$(objectType: ObjectTypes, objectId: number): Observable<boolean> {
    const key = this.getObjectKey(objectType, objectId);
    return this.lockedObjectsSubject.pipe(
      map((lockedMap) => lockedMap.has(key))
    );
  }

  public getLockItem(objectType: ObjectTypes, objectId: number): TimeShiftLockItem | undefined {
    const key = this.getObjectKey(objectType, objectId);
    return this.lockedObjects.get(key);
  }

  public getLockItem$(objectType: ObjectTypes, objectId: number): Observable<TimeShiftLockItem | undefined> {
    const key = this.getObjectKey(objectType, objectId);
    return this.lockedObjectsSubject.pipe(
      map((lockedMap) => lockedMap.get(key))
    );
  }

  public getLockedObjectsByType(objectType: ObjectTypes): TimeShiftLockItem[] {
    return Array.from(this.lockedObjects.values()).filter((item) => item.objectType === objectType);
  }

  public getLockedObjectsByType$(objectType: ObjectTypes): Observable<TimeShiftLockItem[]> {
    return this.lockedObjectsSubject.pipe(
      map((lockedMap) => Array.from(lockedMap.values()).filter((item) => item.objectType === objectType))
    );
  }

  public clearAllLocks(): void {
    this.lockedObjects.clear();
    this.lockedObjectsSubject.next(new Map());
  }

  public syncLockedObject(objectType: ObjectTypes, objectId: number, isLocked: boolean, dayOffset: number = 0): void {
    const key = this.getObjectKey(objectType, objectId);

    if (isLocked) {
      const lockItem: TimeShiftLockItem = {
        objectId,
        objectType,
        isLocked: true,
        dayOffset,
      };
      this.lockedObjects.set(key, lockItem);
    } else {
      this.lockedObjects.delete(key);
    }

    this.lockedObjectsSubject.next(new Map(this.lockedObjects));
  }

  public syncLockedObjects(items: TimeShiftLockItem[]): void {
    this.updateLockedObjects(items);
  }
}
